import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { InsightBar, statusColor } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { formatCurrency } from '../../utils/formatters.js';
import { countBy, sumBy } from '../../utils/insightStats.js';

import { PayrollRunModal } from './PayrollRunModal.jsx';
import { PayslipsModal } from './PayslipsModal.jsx';

export function PayrollPage() {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [payslipsRun, setPayslipsRun] = useState(null);
  const abfrage = useQuery({
    queryKey: ['hr', 'payroll'],
    queryFn: () => hrApi.listPayrollRuns({ limit: 48 }),
  });
  const data = abfrage.data?.items || [];
  const { isLoading, error } = abfrage;
  const meta = abfrage.data?.meta;

  const palette = useChartPalette();
  /**
   * Die Kennzahlen kommen aus der ZÄHLUNG des Dienstes, nicht aus den
   * geladenen Zeilen.
   *
   * Das Repository begrenzt auf 24 Läufe. Eine Firma im dritten
   * Betriebsjahr hat 30 monatliche Läufe - die Lohnsumme über der Tabelle
   * war damit die Summe der 24 jüngsten, und nichts wies darauf hin.
   * Dasselbe Muster wie bei den Ferienanträgen, nur eine Seite weiter.
   */
  const stats = useMemo(() => {
    const rows = data;
    const server = meta?.stats;
    const statuses = server ? Object.keys(server.byStatus || {}) : Object.keys(countBy(rows, (r) => r.status));
    return {
      runs: server ? server.total : rows.length,
      statuses,
      netByStatus: server
        ? statuses.map((s) => Number(server.byStatus[s]?.net ?? 0))
        : statuses.map((s) => sumBy(rows, (r) => r.totalNet, (r) => r.status === s)),
      gross: server ? Number(server.gross) : sumBy(rows, (r) => r.totalGross),
      net: server ? Number(server.net) : sumBy(rows, (r) => r.totalNet),
      /* Abzüge bleiben über die geladenen Zeilen: sie stehen nicht in der
         Aggregation, und eine zweite Abfrage nur dafür wäre ein hoher
         Preis für eine Nebenzahl. Sichtbar gemacht wird es unten. */
      deductions: sumBy(rows, (r) => r.totalDeductions) + sumBy(rows, (r) => r.totalTax),
    };
  }, [data, meta]);

  const queryClient = useQueryClient();
  const [stornoZiel, setStornoZiel] = useState(null);
  const [grund, setGrund] = useState('');
  const [fehler, setFehler] = useState(null);

  /**
   * Ein Lohnlauf wird zurueckgenommen, nicht geloescht.
   *
   * Beide Laeufe bleiben nachweisbar - ein geloeschter waere von einem nie
   * erfolgten nicht mehr zu unterscheiden. Danach ist der Monat wieder frei,
   * und die Buchhaltung storniert ihre Buchung von selbst.
   */
  const storno = useMutation({
    mutationFn: ({ id, reason }) => hrApi.cancelPayrollRun(id, reason),
    onSuccess: () => {
      setStornoZiel(null);
      setGrund('');
      setFehler(null);
      queryClient.invalidateQueries({ queryKey: ['hr', 'payroll'] });
    },
    onError: (e) => setFehler(e.message),
  });

  const columns = [
    {
      key: 'period',
      label: t('hr.payroll.col.period'),
      render: (r) => `${r.periodYear}-${String(r.periodMonth).padStart(2, '0')}`,
    },
    { key: 'status', label: t('col.status'), render: (r) => <Badge status={r.status} /> },
    { key: 'totalGross',      label: t('hr.payroll.col.gross'),      align: 'right', render: (r) => formatCurrency(r.totalGross) },
    /* Die Quellensteuer geht an den Kanton, nicht an die Ausgleichskasse -
       eine eigene Spalte, weil es ein eigener Empfaenger ist. */
    { key: 'totalWithholdingTax', label: t('hr.payroll.col.withholding'), align: 'right', render: (r) => formatCurrency(r.totalWithholdingTax) },
    { key: 'totalDeductions', label: t('hr.payroll.col.deductions'), align: 'right', render: (r) => formatCurrency(r.totalDeductions) },
    { key: 'totalNet',        label: t('hr.payroll.col.net'),        align: 'right', render: (r) => formatCurrency(r.totalNet) },
    /* Was die Firma ZUSAETZLICH zahlt. Ohne diese Spalte sieht ein Lohnlauf
       um rund ein Siebtel guenstiger aus, als er ist - und ein Budget, das
       darauf beruht, ist falsch. */
    { key: 'totalEmployer',   label: t('hr.payroll.col.employer'),   align: 'right', render: (r) => formatCurrency(r.totalEmployer) },
    {
      key: 'actions',
      label: '',
      render: (r) => (
        <span className="row-actions">
          <Button size="sm" variant="ghost" onClick={() => setPayslipsRun(r)}>
            {t('hr.payslips.action')}
          </Button>
          {/* Ein stornierter Lauf laesst sich nicht noch einmal stornieren -
              und der Knopf gar nicht erst anzubieten ist ehrlicher, als ihn
              ins Leere laufen zu lassen. */}
          {r.status !== 'CANCELLED' && (
            <Button size="sm" variant="default" onClick={() => { setStornoZiel(r); setGrund(''); setFehler(null); }}>
              {t('hr.payroll.action.cancel')}
            </Button>
          )}
        </span>
      ),
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('hr.payroll.title')}</h1>
          <p className="muted">{t('hr.payroll.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t('hr.payroll.action.new')}
          </Button>
        </div>
      </header>
      {stats.runs > 0 && (
        <InsightBar
          title={t('insight.byStatus')}
          labels={stats.statuses.map((s) => (s === '-' ? s : t(`status.${s}`)))}
          values={stats.netByStatus}
          colors={stats.statuses.map((s) => statusColor(palette, s))}
          formatValue={(n) => formatCurrency(n)}
          metrics={[
            { label: t('hr.payroll.insight.runs'), value: stats.runs },
            { label: t('hr.payroll.insight.gross'), value: formatCurrency(stats.gross) },
            { label: t('hr.payroll.insight.net'), value: formatCurrency(stats.net) },
            { label: t('hr.payroll.insight.deductions'), value: formatCurrency(stats.deductions) },
          ]}
        />
      )}
      {error && <div className="alert danger">{error.message}</div>}
      <Table columns={columns} rows={data} isLoading={isLoading} emptyMessage={t('empty.payroll')} />
      <PayrollRunModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <PayslipsModal run={payslipsRun} onClose={() => setPayslipsRun(null)} />
          <Modal
        open={Boolean(stornoZiel)}
        onClose={() => { setStornoZiel(null); setFehler(null); }}
        title={t('hr.payroll.cancel.title', {
          period: stornoZiel ? `${stornoZiel.periodYear}-${String(stornoZiel.periodMonth).padStart(2, '0')}` : '',
        })}
        footer={(
          <>
            <Button onClick={() => { setStornoZiel(null); setFehler(null); }}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={storno.isPending}
              onClick={() => storno.mutate({ id: stornoZiel.id, reason: grund })}
            >
              {t('hr.payroll.action.cancel')}
            </Button>
          </>
        )}
      >
        {/* Was gleich passiert - und was NICHT. */}
        <p>{t('hr.payroll.cancel.explain')}</p>
        <label className="field">
          <span className="field-label">{t('hr.payroll.cancel.reason')}</span>
          <input className="input" value={grund} maxLength={300} onChange={(e) => setGrund(e.target.value)} placeholder={t('hr.payroll.cancel.reasonHint')} />
        </label>
        {fehler && <div className="alert danger">{fehler}</div>}
      </Modal>

</section>
  );
}
